const isCapacitorNative =
  typeof window !== "undefined" &&
  window.Capacitor?.isNativePlatform?.() === true;

export class LocationError extends Error {
  constructor(code, message) {
    super(message);
    this.name = "LocationError";
    this.code = code;
  }
}

const ERROR_CODE_MAP = {
  1: "DENIED",
  2: "UNAVAILABLE",
  3: "TIMEOUT",
};

export async function getCurrentLocation({
  timeoutMs = 10000,
  enableHighAccuracy = true,
  maximumAgeMs = 60000,
} = {}) {
  if (isCapacitorNative) {
    const { Geolocation } = await import("@capacitor/geolocation");
    const pos = await Geolocation.getCurrentPosition({
      timeout: timeoutMs,
      enableHighAccuracy,
      maximumAge: maximumAgeMs,
    });
    return {
      lat: pos.coords.latitude,
      lng: pos.coords.longitude,
      accuracy: pos.coords.accuracy,
      source: "native",
    };
  }

  if (typeof navigator === "undefined" || !("geolocation" in navigator)) {
    throw new LocationError("UNSUPPORTED", "Geolocation is not available");
  }

  if ("permissions" in navigator) {
    try {
      const status = await navigator.permissions.query({ name: "geolocation" });
      if (status.state === "denied") {
        throw new LocationError(
          "DENIED",
          "Location permission has been denied",
        );
      }
    } catch (e) {
      if (e instanceof LocationError) throw e;
    }
  }

  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          source: "web",
        }),
      (err) => {
        const code = ERROR_CODE_MAP[err.code] || "UNKNOWN";
        reject(new LocationError(code, err.message));
      },
      { timeout: timeoutMs, enableHighAccuracy, maximumAge: maximumAgeMs },
    );
  });
}

export function userMessageForLocationError(err) {
  if (!(err instanceof LocationError)) {
    return "Couldn't fetch your location. Please pick on the map instead.";
  }
  switch (err.code) {
    case "DENIED":
      return "Location access was denied. Please enable location for this app, or pick on the map.";
    case "UNAVAILABLE":
      return "Your location is unavailable right now. Please pick on the map.";
    case "TIMEOUT":
      return "Locating took too long. Please try again or pick on the map.";
    case "UNSUPPORTED":
      return "Your browser doesn't support location. Please pick on the map.";
    default:
      return "Couldn't fetch your location. Please pick on the map instead.";
  }
}
